import Vue from "vue";
import { mapState } from "vuex";

import gridComponent from "@/components/child/grid/grid.vue";
import { GridAction } from "@/components/child/grid/gridAction";
import {
    GridColors,
    GridColumn,
    GridColumnDecimal,
    GridColumnExt,
    GridColumnType,
} from "@/components/child/grid/gridColumn";
import { Component, Prop, Watch } from "@/decorators";
import SNGPCProdutoDetalhesModel from "@/models/sngpc/sNGPCProdutoDetalhesModel";
import SNGPCProdutoModel from "@/models/sngpc/sNGPCProdutoModel";
import UsuarioModel from "@/models/usuarioModel";
import SNGPCService from "@/services/sngpc/SNGPCService";
import { AppState } from "@/store/store";
import { sortArray } from "@/utils/common/array";

import "@/components/parent/crud/crud.scss";

@Component({
    components: {
        gridComponent,
    },
    computed: {
        ...mapState({
            usuarioLogado: (state: AppState) => state.session.usuarioLogado,
        }),
    },
})
export default class SNGPCProdutoListComponent extends Vue {
    usuarioLogado: UsuarioModel;

    gridData: Array<SNGPCProdutoModel> = [];
    private quantidadesOriginais: Record<number, number> = {};
    private quantidadesEditadas: Record<number, number> = {};
    private editingRowIndex = -1;

    @Prop({ default: false }) isPendente: boolean;

    private get isGrupoAdmin(): boolean {
        return this.usuarioLogado?.grupos?.some(g => g.descricao === "Admin") ?? false;
    }

    private get podeEditarQuantidade(): boolean {
        return this.isPendente && this.isGrupoAdmin;
    }

    @Watch("isPendente")
    @Watch("usuarioLogado")
    private onPermissaoChanged() {
        this.rebuildSubActions();
    }

    get gridColumns(): Array<GridColumn> {
        return [
            new GridColumn("dataInicial", "Inicio", GridColumnType.Date),
            new GridColumn("dataFinal", "Fim", GridColumnType.Date),
            new GridColumn("produtoDescricao", "Produto", GridColumnType.String),
            new GridColumnDecimal("quantidadeMovimentadaTotal", "Quantidade", 4),
        ];
    }

    subDataName = "sngpcDetalhes";

    get gridColumnsVinculos(): Array<GridColumn> {
        const emEdicao = this.editingRowIndex >= 0;

        const colunaQuantidade: GridColumn = emEdicao
            ? new GridColumnExt({
                  value: "quantidadeMovimentada",
                  text: "Quantidade",
                  type: GridColumnType.Decimal,
                  casasDecimais: 6,
                  editable: true,
                  disabled: false,
                  onChange: (valor: number, rowIndex: number, rowSubIndex: number) => {
                      const detalhe = (this.gridData[rowIndex] as any)?.["sngpcDetalhes"]?.[rowSubIndex];
                      if (detalhe) {
                          this.quantidadesEditadas[detalhe.id] = valor;
                      }
                  },
              })
            : new GridColumnDecimal("quantidadeMovimentada", "Quantidade", 6);

        return [
            new GridColumn("dataMovimento", "Data", GridColumnType.Date),
            new GridColumn("tipoMovimentoDescricao", "Movimento", GridColumnType.String),
            colunaQuantidade,
            new GridColumn("unidadeFarmacotecnicaDescricao", "Unidade", GridColumnType.String),
            new GridColumnDecimal("quantidadeDeUnidadesFarmacotecnicas", "Volume", 4),
            new GridColumnDecimal("quantidadeDeInsumoPorUnidadeFarmacotecnica", "Quantidade Insumo", 4),
            new GridColumn("unidadeDeMedidaDoInsumoDescricao", "Unidade Insumo", GridColumnType.String),
            new GridColumn("loteDescricao", "Lote", GridColumnType.String),
        ];
    }

    extraSubActions: Array<Array<GridAction>> = [];
    private extraSubActionsEditar: Array<GridAction> = [];
    private extraSubActionsConfirmar: Array<GridAction> = [];
    private extraSubActionsCancelar: Array<GridAction> = [];

    conditionShowSubData(it) {
        return it["sngpcDetalhes"] && it["sngpcDetalhes"]["length"] > 0;
    }

    private onChangeSort(sortKey: keyof SNGPCProdutoModel, sortOrder: "asc" | "desc") {
        sortArray(this.gridData, sortKey, sortOrder);
    }

    public loadModel(model: Array<SNGPCProdutoModel>) {
        this.editingRowIndex = -1;
        this.quantidadesOriginais = {};
        this.quantidadesEditadas = {};
        this.gridData = [];
        this.gridData = model;
        this.rebuildSubActions();
    }

    private rebuildSubActions() {
        if (!this.podeEditarQuantidade || !this.gridData?.length) {
            this.extraSubActions = [];
            return;
        }

        this.extraSubActionsEditar.splice(0);
        this.extraSubActionsConfirmar.splice(0);
        this.extraSubActionsCancelar.splice(0);

        for (let i = 0; i < this.gridData.length; i++) {
            const emEdicao = i === this.editingRowIndex;

            this.extraSubActionsEditar.push(
                new GridAction("editar-quantidade", "Editar quantidades", "fa fa-edit", GridColors.BLUE, !emEdicao),
            );
            this.extraSubActionsConfirmar.push(
                new GridAction("confirmar-quantidade", "Confirmar", "fa fa-check", GridColors.GREEN, emEdicao),
            );
            this.extraSubActionsCancelar.push(
                new GridAction("cancelar-quantidade", "Cancelar", "fa fa-times", GridColors.RED, emEdicao),
            );
        }

        this.extraSubActions.splice(0);
        this.extraSubActions.push(this.extraSubActionsEditar);
        this.extraSubActions.push(this.extraSubActionsConfirmar);
        this.extraSubActions.push(this.extraSubActionsCancelar);
    }

    private async onExtraSubAction(name: string, entry: SNGPCProdutoDetalhesModel, rowIndex: number) {
        if (name === "editar-quantidade") {
            this.editingRowIndex = rowIndex;
            this.quantidadesEditadas = {};
            this.quantidadesOriginais = {};

            const detalhes = (this.gridData[rowIndex] as any)?.["sngpcDetalhes"] ?? [];
            for (const d of detalhes) {
                this.quantidadesOriginais[d.id] = d.quantidadeMovimentada;
            }

            this.rebuildSubActions();
        } else if (name === "cancelar-quantidade") {
            const detalhes = (this.gridData[this.editingRowIndex] as any)?.["sngpcDetalhes"] ?? [];
            for (const d of detalhes) {
                if (this.quantidadesOriginais[d.id] !== undefined) {
                    d.quantidadeMovimentada = this.quantidadesOriginais[d.id];
                }
            }

            this.editingRowIndex = -1;
            this.quantidadesOriginais = {};
            this.quantidadesEditadas = {};
            this.rebuildSubActions();
        } else if (name === "confirmar-quantidade") {
            await this.onConfirmarEdicao(rowIndex);
        }
    }

    private async onConfirmarEdicao(rowIndex: number) {
        const produto = this.gridData[rowIndex];
        const detalhes = (produto as any)?.["sngpcDetalhes"] ?? [];

        for (const d of detalhes) {
            if (this.quantidadesEditadas[d.id] !== undefined) {
                d.quantidadeMovimentada = this.quantidadesEditadas[d.id];
            }
        }

        if (detalhes.some(d => d.quantidadeMovimentada === null || d.quantidadeMovimentada < 0)) {
            await this.$showError(this.$t("__.ts.erro") as string, "Nenhuma quantidade pode ser negativa.");
            return;
        }

        const alterados = detalhes.filter(
            d =>
                this.quantidadesOriginais[d.id] !== undefined &&
                d.quantidadeMovimentada !== this.quantidadesOriginais[d.id],
        );

        if (alterados.length === 0) {
            this.editingRowIndex = -1;
            this.quantidadesOriginais = {};
            this.quantidadesEditadas = {};
            this.rebuildSubActions();
            return;
        }

        try {
            for (const detalhe of alterados) {
                await SNGPCService.updateQuantidadeDetalhe(detalhe.id, detalhe.quantidadeMovimentada)
                    .withLoading()
                    .resolveWithoutJSON();
            }

            produto.quantidadeMovimentadaTotal = detalhes.reduce((acc, d) => acc + d.quantidadeMovimentada, 0);

            this.editingRowIndex = -1;
            this.quantidadesOriginais = {};
            this.quantidadesEditadas = {};
            this.rebuildSubActions();

            await this.$showSuccess(this.$t("__.ts.sucesso") as string, "Quantidade(s) atualizada(s) com sucesso.");
        } catch {}
    }
}
